/**
 * Where a song hands over to the next one: the one definition every player and
 * planner uses (2026-10-29), so what Radio's clock shows is what the deck does.
 *
 * With a crossfade, the next song starts at the song's own mix point when it has
 * one (the Liquidsoap-style cues: Settings -> Library -> Cue points), and the two
 * overlap until the Early Out; without a mix point, one crossfade before the Early
 * Out. With no crossfade (gapless) it is always the Early Out. The engine's
 * `Track.plays_for` (nova/flow/curate.py) is the same rule.
 */

export interface HandoverTrack {
  cueIn: number | null;
  cueOut: number | null;
  duration: number;
  /** The mix point in force (seconds into the file), or none. */
  mixAt?: number | null;
}

/** The Early Out, else the end of the file. */
export function endOf(t: HandoverTrack): number {
  return t.cueOut ?? t.duration;
}

/** The mix point when it applies: a crossfade is set, and it falls after the Cue In (5 s) and before the end. */
export function mixPoint(t: HandoverTrack, crossfade: number): number | null {
  const mix = t.mixAt ?? null;
  if (crossfade <= 0 || mix === null) return null;
  return mix < endOf(t) && mix > (t.cueIn ?? 0) + 5 ? mix : null;
}

/** Seconds into the file at which the next song starts. */
export function handoverAt(t: HandoverTrack, crossfade: number): number {
  return mixPoint(t, crossfade) ?? endOf(t) - Math.max(0, crossfade);
}

/** How long the two overlap: from the hand-over to the end. */
export function overlapOf(t: HandoverTrack, crossfade: number): number {
  const mix = mixPoint(t, crossfade);
  return mix !== null ? endOf(t) - mix : Math.max(0, crossfade);
}

/** Seconds from the Cue In to the hand-over, never under 5 (what a planner counts). */
export function playsFor(t: HandoverTrack, crossfade: number): number {
  return Math.max(5, handoverAt(t, crossfade) - (t.cueIn ?? 0));
}
